import React, { useState, useEffect } from 'react';
import { ArrowRightLeft, AlertCircle, ShieldAlert } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Combobox } from '@/components/common/Combobox';
import { transferService } from '@/services/transferService';
import { cycleService } from '@/services/cycleService';
import { ProductionCycleRecord, ProductionUnitRecord } from '@/types/database';
import { useAuth } from '@/context/AuthContext';

interface PigTransferModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (transfer: any) => void;
  defaultCycleId?: string | null;
  defaultCycleCode?: string;
  sourceLiveHeadcount?: number;
}

export const PigTransferModal: React.FC<PigTransferModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  defaultCycleId,
  defaultCycleCode,
  sourceLiveHeadcount,
}) => {
  const { isAdmin } = useAuth();

  const [transferDate, setTransferDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [sourceCycleId, setSourceCycleId] = useState<string>(defaultCycleId || '');
  const [destinationCycleId, setDestinationCycleId] = useState<string>('');
  const [sourceUnitId, setSourceUnitId] = useState<string>('');
  const [destinationUnitId, setDestinationUnitId] = useState<string>('');
  const [headCount, setHeadCount] = useState<number | ''>(5);
  const [totalWeightKg, setTotalWeightKg] = useState<number | ''>('');
  const [reason, setReason] = useState<string>('');
  const [notes, setNotes] = useState<string>('');

  const [cycles, setCycles] = useState<ProductionCycleRecord[]>([]);
  const [productionUnits, setProductionUnits] = useState<ProductionUnitRecord[]>([]);

  // Admin Override state
  const [adminOverrideReason, setAdminOverrideReason] = useState<string>('');
  const [confirmAdminOverride, setConfirmAdminOverride] = useState<boolean>(false);

  const [loading, setLoading] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    if (defaultCycleId) {
      setSourceCycleId(defaultCycleId);
    }
  }, [defaultCycleId]);

  useEffect(() => {
    if (!isOpen) return;
    setErrorMsg(null);
    setAdminOverrideReason('');
    setConfirmAdminOverride(false);

    cycleService.getCycles({ status: 'active' }).then((c) => {
      setCycles(c);
      if (!sourceCycleId && c.length > 0) {
        setSourceCycleId(c[0].id);
      }
    });

    cycleService.getProductionUnits().then((units) => {
      setProductionUnits(units);
    });
  }, [isOpen]);

  const targetSourceHead =
    typeof sourceLiveHeadcount === 'number' && sourceCycleId === defaultCycleId
      ? sourceLiveHeadcount
      : undefined;

  const isExceedingSourceHead =
    targetSourceHead !== undefined && Number(headCount) > targetSourceHead;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);

    if (!transferDate) {
      setErrorMsg('Transfer date is required.');
      return;
    }
    if (!headCount || Number(headCount) <= 0) {
      setErrorMsg('Transfer head count must be greater than zero.');
      return;
    }
    if (!reason.trim()) {
      setErrorMsg('Transfer reason is required.');
      return;
    }
    if (!sourceCycleId && !destinationCycleId) {
      setErrorMsg('Please specify at least a source cycle or a destination cycle.');
      return;
    }
    if (
      sourceCycleId &&
      destinationCycleId &&
      sourceCycleId === destinationCycleId &&
      sourceUnitId === destinationUnitId
    ) {
      setErrorMsg('Source and Destination cannot be the exact same cycle and pen.');
      return;
    }

    // Enforce impossible-headcount rule
    if (isExceedingSourceHead) {
      if (!isAdmin) {
        setErrorMsg(
          `Impossible Headcount Balance: Transfer count (${headCount}) exceeds current live headcount in source cycle (${targetSourceHead} head). Operations users cannot post negative live balances. Please contact an Administrator to review or override.`
        );
        return;
      }
      if (!confirmAdminOverride || !adminOverrideReason.trim()) {
        setErrorMsg('Admin override requires a mandatory justification reason.');
        return;
      }
    }

    setLoading(true);
    try {
      let finalNotes = notes.trim();
      if (isExceedingSourceHead && isAdmin) {
        finalNotes = `[Admin Override: ${adminOverrideReason.trim()}] ${finalNotes}`.trim();
      }

      const created = await transferService.createTransfer({
        transfer_date: transferDate,
        source_cycle_id: sourceCycleId || null,
        destination_cycle_id: destinationCycleId || null,
        source_unit_id: sourceUnitId || null,
        destination_unit_id: destinationUnitId || null,
        head_count: Number(headCount),
        total_weight_kg: totalWeightKg !== '' ? Number(totalWeightKg) : null,
        reason: reason.trim(),
        notes: finalNotes || null,
      });

      onSuccess(created);
      onClose();
    } catch (err: any) {
      setErrorMsg(err.message || 'Failed to create pig transfer.');
    } finally {
      setLoading(false);
    }
  };

  const cycleOptions = cycles.map((c) => ({
    value: c.id,
    label: `${c.cycle_code} (${c.growers?.full_name || 'Company Owned'})`,
  }));

  const unitOptions = productionUnits.map((u) => ({
    value: u.id,
    label: `${u.unit_name}${u.unit_code ? ` (${u.unit_code})` : ''}`,
  }));

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto p-4 sm:p-6">
        <DialogHeader className="border-b border-slate-200 pb-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-purple-100 text-purple-800 flex items-center justify-center font-bold">
              <ArrowRightLeft className="w-4 h-4" />
            </div>
            <div>
              <DialogTitle className="text-base sm:text-lg font-bold text-slate-900">
                Log Pig Transfer
              </DialogTitle>
              <p className="text-xs text-slate-500">
                Record movement of animals between production cycles or internal pens.
              </p>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 pt-2">
          {errorMsg && (
            <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-xs flex items-start gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{errorMsg}</span>
            </div>
          )}

          <div>
            <label className="text-xs font-medium text-slate-700 block mb-1">Transfer Date *</label>
            <Input
              type="date"
              value={transferDate}
              onChange={(e) => setTransferDate(e.target.value)}
              className="h-10 text-xs font-mono"
              required
            />
          </div>

          {/* Source Location */}
          <div className="p-3.5 bg-slate-50 rounded-lg border border-slate-200 space-y-2.5">
            <div className="flex items-center justify-between">
              <label className="text-xs font-bold text-slate-800 uppercase tracking-wider block">
                Source (Transferred From)
              </label>
              {targetSourceHead !== undefined && (
                <span className="text-[11px] font-mono text-slate-600">
                  Live: <strong>{targetSourceHead} head</strong>
                </span>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] font-medium text-slate-600 block mb-1">Source Cycle</label>
                <Combobox
                  options={cycleOptions}
                  value={sourceCycleId}
                  onChange={setSourceCycleId}
                  allowAll={true}
                  allLabel="None / External Source"
                  placeholder="Select Source Cycle..."
                  searchPlaceholder="Search cycle..."
                />
              </div>

              <div>
                <label className="text-[11px] font-medium text-slate-600 block mb-1">Source Pen (Optional)</label>
                <Combobox
                  options={unitOptions}
                  value={sourceUnitId}
                  onChange={setSourceUnitId}
                  allowAll={true}
                  allLabel="Any / Default"
                  placeholder="Select Source Pen..."
                  searchPlaceholder="Search pen..."
                />
              </div>
            </div>
          </div>

          {/* Destination Location */}
          <div className="p-3.5 bg-slate-50 rounded-lg border border-slate-200 space-y-2.5">
            <label className="text-xs font-bold text-slate-800 uppercase tracking-wider block">
              Destination (Transferred To)
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] font-medium text-slate-600 block mb-1">Destination Cycle</label>
                <Combobox
                  options={cycleOptions}
                  value={destinationCycleId}
                  onChange={setDestinationCycleId}
                  allowAll={true}
                  allLabel="None / External Destination"
                  placeholder="Select Destination Cycle..."
                  searchPlaceholder="Search cycle..."
                />
              </div>

              <div>
                <label className="text-[11px] font-medium text-slate-600 block mb-1">Destination Pen (Optional)</label>
                <Combobox
                  options={unitOptions}
                  value={destinationUnitId}
                  onChange={setDestinationUnitId}
                  allowAll={true}
                  allLabel="Any / Default"
                  placeholder="Select Destination Pen..."
                  searchPlaceholder="Search pen..."
                />
              </div>
            </div>
          </div>

          {/* Head Count & Weight */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold text-slate-800 block mb-1">Head Count Transferred *</label>
              <Input
                type="number"
                min="1"
                step="1"
                tabular
                inputMode="numeric"
                value={headCount}
                onChange={(e) =>
                  setHeadCount(e.target.value === '' ? '' : parseInt(e.target.value, 10))
                }
                placeholder="5"
                className="h-10 text-xs font-mono font-bold"
                required
              />
            </div>

            <div>
              <label className="text-xs font-medium text-slate-700 block mb-1">
                Total Weight (kg) <span className="text-slate-400 font-normal">(Optional)</span>
              </label>
              <Input
                type="number"
                step="0.1"
                min="0"
                tabular
                inputMode="decimal"
                value={totalWeightKg}
                onChange={(e) =>
                  setTotalWeightKg(e.target.value === '' ? '' : parseFloat(e.target.value))
                }
                placeholder="e.g. 250.0"
                className="h-10 text-xs font-mono"
              />
            </div>
          </div>

          {/* Impossible-Negative Headcount Guard */}
          {isExceedingSourceHead && (
            <div className="p-3.5 rounded-lg bg-red-50 border border-red-300 text-red-900 text-xs space-y-2.5">
              <div className="flex items-start gap-2 font-bold text-red-800">
                <ShieldAlert className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                <span>
                  Impossible Balance: Transfer count ({headCount}) exceeds source cycle live balance ({targetSourceHead} head).
                </span>
              </div>

              {!isAdmin ? (
                <p className="text-red-700 leading-snug">
                  Operations role cannot post negative live headcount transactions. Please check your count or contact an Administrator to review.
                </p>
              ) : (
                <div className="space-y-2 pt-1 border-t border-red-200">
                  <p className="text-slate-800 font-semibold">
                    Administrator Override Mode: Mandatory justification required.
                  </p>
                  <div>
                    <label className="text-[11px] font-bold text-slate-700 block mb-1">
                      Mandatory Override Reason *
                    </label>
                    <Input
                      type="text"
                      value={adminOverrideReason}
                      onChange={(e) => setAdminOverrideReason(e.target.value)}
                      placeholder="e.g. Historical count discrepancy, physical verification confirmed..."
                      className="h-10 text-xs bg-white"
                      required
                    />
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer font-medium text-slate-900 pt-1 min-h-[44px]">
                    <input
                      type="checkbox"
                      checked={confirmAdminOverride}
                      onChange={(e) => setConfirmAdminOverride(e.target.checked)}
                      className="rounded text-red-600 focus:ring-red-500 w-4 h-4"
                    />
                    <span>I confirm this Administrative Override and accept the audit record</span>
                  </label>
                </div>
              )}
            </div>
          )}

          <div>
            <label className="text-xs font-medium text-slate-700 block mb-1">Reason for Transfer *</label>
            <Input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Pen balancing, health segregation, grower facility capacity adjustment"
              className="h-10 text-xs"
              required
            />
          </div>

          <div>
            <label className="text-xs font-medium text-slate-700 block mb-1">Notes</label>
            <Input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Transport vehicle, personnel involved, pen notes..."
              className="h-10 text-xs"
            />
          </div>

          <DialogFooter className="sticky bottom-0 bg-white border-t border-slate-200 pt-3 flex flex-row items-center justify-end gap-2 z-10">
            <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={loading} className="min-h-[44px]">
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              className="bg-purple-700 hover:bg-purple-800 font-semibold gap-1 text-white min-h-[44px]"
              disabled={loading || (isExceedingSourceHead && (!isAdmin || !confirmAdminOverride || !adminOverrideReason.trim()))}
            >
              <ArrowRightLeft className="w-4 h-4" /> {loading ? 'Transferring...' : 'Execute Transfer'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
